const DEFAULT_PAGE_ID = 'page 1';
const DEFAULT_BLOCK_X = 100;
const DEFAULT_BLOCK_Y = 100;
const DEFAULT_BLOCK_SPACING = 20;

const SPRITE_DEFAULTS = Object.freeze({
    Penguin: {cx: 27, cy: 35, w: 54, h: 70, scale: 0.5},
    Bluux: {cx: 27, cy: 35, w: 54, h: 70, scale: 0.5},
    Cat: {cx: 35, cy: 30, w: 70, h: 60, scale: 0.7},
    Dog: {cx: 45, cy: 40, w: 90, h: 80, scale: 0.7},
    Bird: {cx: 37, cy: 30, w: 75, h: 60, scale: 0.7},
    Crab: {cx: 42, cy: 35, w: 85, h: 70, scale: 0.7},
    MoonFlag: {cx: 18, cy: 48, w: 36, h: 96, scale: 0.78},
    default: {cx: 30, cy: 35, w: 60, h: 70, scale: 0.6}
});

export function buildProjectRecordFromTemplate (title, template, version = 'iOSv01') {
    const json = buildProjectJsonFromTemplate(template);
    const firstPage = json[DEFAULT_PAGE_ID] || {};

    return {
        name: title,
        version,
        thumbnail: {
            pagecount: (json.pages && json.pages.length) || 1,
            md5: firstPage.md5 || 'backgrounds/Farm.svg'
        },
        json
    };
}

export function buildProjectJsonFromTemplate (template) {
    const pages = Array.isArray(template && template.pages) && template.pages.length > 0
        ? template.pages
        : [{background: 'backgrounds/Farm.svg', sprites: []}];
    const project = {
        pages: [],
        currentPage: DEFAULT_PAGE_ID
    };

    pages.forEach((pageTemplate, index) => {
        const pageId = `page ${index + 1}`;
        project.pages.push(pageId);
        project[pageId] = buildPageFromTemplate(pageTemplate, pageId);
    });

    project.currentPage = project.pages[0] || DEFAULT_PAGE_ID;
    return project;
}

export function buildPageFromTemplate (pageTemplate, pageId) {
    const sprites = Array.isArray(pageTemplate && pageTemplate.sprites) ? pageTemplate.sprites : [];
    const preferredSpriteIndex = getPreferredSpriteIndex(sprites);
    const page = {
        textstartat: 36,
        sprites: [],
        md5: normalizeBackground(pageTemplate && pageTemplate.background),
        num: Number(String(pageId).replace('page ', '')) || 1,
        lastSprite: '',
        currentSpriteName: '',
        layers: []
    };
    let playerSpriteId = '';

    sprites.forEach((spriteTemplate, index) => {
        const baseName = spriteTemplate && spriteTemplate.name ? spriteTemplate.name : 'Penguin';
        const spriteId = `${baseName} ${index + 1}`;
        const defaults = getSpriteDefaults(baseName, spriteTemplate);
        const x = typeof spriteTemplate.x === 'number' ? spriteTemplate.x : 240;
        const y = typeof spriteTemplate.y === 'number' ? spriteTemplate.y : 180;
        const role = spriteTemplate && spriteTemplate.role ? spriteTemplate.role : '';

        page.sprites.push(spriteId);
        page.layers.push(spriteId);
        if (!playerSpriteId && role === 'player') {
            playerSpriteId = spriteId;
        }
        if (index === preferredSpriteIndex || !page.lastSprite) {
            page.lastSprite = spriteId;
        }

        page[spriteId] = {
            shown: true,
            type: 'sprite',
            md5: normalizeSprite(spriteTemplate && spriteTemplate.md5, baseName),
            id: spriteId,
            flip: false,
            name: baseName,
            role: spriteTemplate && spriteTemplate.role ? spriteTemplate.role : undefined,
            angle: 0,
            scale: defaults.scale,
            speed: 2,
            defaultScale: defaults.scale,
            sounds: ['pop.mp3'],
            xcoor: x,
            ycoor: y,
            cx: defaults.cx,
            cy: defaults.cy,
            w: defaults.w,
            h: defaults.h,
            homex: x,
            homey: y,
            homescale: defaults.scale,
            homeshown: true,
            homeflip: false,
            role,
            scripts: encodeTemplateScripts(spriteTemplate && spriteTemplate.scripts)
        };
    });

    page.lastSprite = playerSpriteId || page.lastSprite || page.sprites[0] || '';
    page.currentSpriteName = page.lastSprite;

    return page;
}

export function encodeTemplateScripts (scripts) {
    if (!Array.isArray(scripts)) {
        return [];
    }

    return scripts
        .map((strip) => encodeTemplateStrip(strip))
        .filter((strip) => Array.isArray(strip) && strip.length > 0);
}

export function encodeTemplateStrip (strip, depth = 0) {
    if (!Array.isArray(strip) || strip.length < 1) {
        return [];
    }

    if (Array.isArray(strip[0])) {
        return JSON.parse(JSON.stringify(strip));
    }

    return strip
        .map((block, index) => encodeTemplateBlock(block, index, depth))
        .filter(Boolean);
}

export function encodeTemplateBlock (block, index = 0, depth = 0) {
    if (!block) {
        return null;
    }

    if (Array.isArray(block)) {
        return JSON.parse(JSON.stringify(block));
    }

    const blockType = block.blocktype || block.type;
    if (!blockType) {
        return null;
    }

    const arg = Object.prototype.hasOwnProperty.call(block, 'arg') ? block.arg : null;
    const x = typeof block.x === 'number'
        ? block.x
        : (typeof block.dx === 'number' ? block.dx : DEFAULT_BLOCK_X + (index * DEFAULT_BLOCK_SPACING));
    const y = typeof block.y === 'number'
        ? block.y
        : (typeof block.dy === 'number' ? block.dy : DEFAULT_BLOCK_Y + (depth * DEFAULT_BLOCK_SPACING));
    const encoded = [blockType, arg == null ? 'null' : arg, x, y];

    if (blockType === 'repeat') {
        const inside = Array.isArray(block.inside)
            ? block.inside
            : (Array.isArray(block.blocks) ? block.blocks : []);
        encoded.push(encodeTemplateStrip(inside, depth + 1));
    }

    return encoded;
}

export function normalizeBackground (background) {
    if (!background) {
        return 'backgrounds/Farm.svg';
    }
    if (background.indexOf('/') > -1) {
        return background;
    }
    return `backgrounds/${background}`;
}

export function normalizeSprite (spriteMd5, spriteName) {
    if (spriteMd5) {
        return spriteMd5;
    }
    return `${spriteName || 'Penguin'}.svg`;
}

export function getSpriteDefaults (spriteName, spriteTemplate) {
    const base = SPRITE_DEFAULTS[spriteName] || SPRITE_DEFAULTS.default;
    return {
        cx: typeof spriteTemplate.cx === 'number' ? spriteTemplate.cx : base.cx,
        cy: typeof spriteTemplate.cy === 'number' ? spriteTemplate.cy : base.cy,
        w: typeof spriteTemplate.w === 'number' ? spriteTemplate.w : base.w,
        h: typeof spriteTemplate.h === 'number' ? spriteTemplate.h : base.h,
        scale: typeof spriteTemplate.scale === 'number' ? spriteTemplate.scale : base.scale
    };
}

export function getPreferredSpriteIndex (sprites) {
    if (!Array.isArray(sprites) || sprites.length < 1) {
        return 0;
    }

    const explicitIndex = sprites.findIndex((sprite) => sprite && sprite.defaultSelected);
    if (explicitIndex > -1) {
        return explicitIndex;
    }

    const playerIndex = sprites.findIndex((sprite) => sprite && sprite.role === 'player');
    if (playerIndex > -1) {
        return playerIndex;
    }

    return 0;
}
